// Angular
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';

// Angular Material
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';

// Third party
import * as _ from 'lodash';
import moment from 'moment';
import { take, takeUntil } from 'rxjs/operators';

// Local library
import {
  CountdownPipe,
  GetEventStatusPipe,
  GetRaceStatusPipe,
  GetTrackTypePipe,
  MatNotificationService,
  NumberWithCommasPipe,
  TranslatePipe,
  UnsubscribeObservableService
} from '@amwager/util';
import { IDataProduct, IGetEventsDetailsResponse } from '@amwager/util-ts';
import { SessionService } from '@amwager/data-access';
import { ProfileComponent } from '@amwager/account/feature-mat';
import { ProfileService } from '@amwager/account/data-access';
import {
  IDataProgramDownload,
  IDataPrograms,
  IProgramTrack,
  ProgramService,
  WagerFacade,
  WagerService
} from '@amwager/wager/data-access';

// Local file
import { ProgramTermsComponent } from './program-terms/program-terms.component';

/** Hand-off pushed by the wager page PRGM link */
interface IProgramHandOff {
  eventId?: string;
  eventName?: string;
  date?: string;
}

/**
 * Program page — browse, buy and read the Equibase data products
 * published for a race date. Port of the legacy Program Shop tab.
 */
@Component({
  selector: 'feature-mat-program',
  templateUrl: './program.component.html',
  styleUrls: ['./program.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    CountdownPipe,
    GetEventStatusPipe,
    GetRaceStatusPipe,
    GetTrackTypePipe,
    NumberWithCommasPipe,
    ProfileComponent,
    TranslatePipe
  ]
})
export class ProgramComponent extends UnsubscribeObservableService
  implements OnInit, OnDestroy {

  /** Programs are only published up to today, so the picker stops there */
  readonly maxDate = new Date();

  /** Scroll container for the horizontally-overflowing favorites row */
  @ViewChild('favorites') favorites!: ElementRef;

  programForm!: FormGroup;

  /** Every product published for the selected date */
  products: IDataProduct[] = [];

  /** Products left after the track filter — what the table renders */
  filteredProducts: IDataProduct[] = [];

  /** Tracks derived from the current product list */
  tracks: IProgramTrack[] = [];

  /** Track options with the bettor's favorites hoisted to the top */
  sortedTracks: IProgramTrack[] = [];

  /** Leading favorites in the options — places the dropdown divider */
  favoriteTrackCount: number = 0;

  /** Selected track event id, empty for all tracks */
  selectedTrackId: string = '';

  /** Favorite event ids from the account preferences */
  setListFavorites!: Set<string>;

  /** Event objects backing the favorites bar chips */
  listOfFavoriteEvents: IGetEventsDetailsResponse[] = [];

  /** Cached user email (`undefined` = unbootstrapped, `Guest` = anonymous) */
  userEmail?: string;

  isLoadingPrograms: boolean = true;
  isLoadingPdf: boolean = false;

  /** Row currently rendered in the viewer */
  activeProductId: string = '';

  /** Shown in the viewer pane when a download fails */
  downloadError: string = '';

  /** Sanitized blob URL handed to the viewer iframe */
  pdfUrl?: SafeResourceUrl;

  /** File name used when saving the open PDF */
  pdfFileName: string = '';

  // True when the inline profile panel takes the page over
  isProfileOpen: boolean = false;

  /** Track hand-off from the wager page, applied on the first response */
  private pendingHandOff?: IProgramHandOff;

  /** Raw object URL behind `pdfUrl` — revoked before it is replaced */
  private pdfObjectUrl: string = '';

  /** Store slice backing the product list, kept for the refresh guard */
  private programsState?: IDataPrograms;

  /** Row the open PDF belongs to */
  private activeProduct?: IDataProduct;

  /** Live events from the store — intersected with the favorite ids */
  private liveEvents: IGetEventsDetailsResponse[] = [];

  /**
   * Flips true on the first favorites emission, even an empty one.
   * Distinguishes "still loading" from "user has no favorites".
   */
  private hasEverLoadedFavorites: boolean = false;

  /**
   * Convenience getter for easy access to form fields
   * @returns {object} Form controls
   */
  get formVal() { return this.programForm?.controls; }

  /**
   * True only while the preferences stream hasn't emitted yet.
   * Hidden for guests — favorites are a signed-in feature.
   * @returns {boolean} true = show skeleton chips
   */
  get showFavoritesSkeleton(): boolean {
    if (!this.userEmail || this.userEmail === 'Guest') return false;

    return !this.hasEverLoadedFavorites;
  }

  constructor(
    private readonly programService: ProgramService,
    private readonly wagerFacade: WagerFacade,
    private readonly wagerService: WagerService,
    private readonly sessionService: SessionService,
    private readonly profileService: ProfileService,
    private readonly matNotificationService: MatNotificationService,
    private readonly sanitizer: DomSanitizer,
    private readonly dialog: MatDialog,
    private readonly cdRef: ChangeDetectorRef,
    private readonly router: Router
  ) {
    super();

    // Route state is only readable during the navigation itself.
    const state = this.router.getCurrentNavigation()?.extras?.state as
      IProgramHandOff | undefined;

    if (state?.eventId || state?.eventName || state?.date) {
      this.pendingHandOff = state;
    }
  }

  ngOnInit(): void {
    // Favorites bar gate — hidden for guests / pre-session.
    this.userEmail = this.sessionService.getUserEmail();

    this.programForm = this.programService.buildForm();

    if (this.pendingHandOff?.date) {
      const handOffDate = moment(this.pendingHandOff.date);

      if (handOffDate.isValid() && !handOffDate.isAfter(this.maxDate, 'day')) {
        this.formVal['date'].setValue(handOffDate.toDate());
      }
    }

    // A PDF left in the store by an earlier visit must not reopen itself.
    this.programService.clearDownload('ProgramComponent:ngOnInit');

    this.observePrograms();
    this.observeDownloads();
    this.observeEventsAndFavorites();

    this.profileService.showProfile$
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((show: boolean) => {
        this.isProfileOpen = show;
        this.cdRef.markForCheck();
      });

    this.fetchPrograms();
  }

  override ngOnDestroy(): void {
    this.toUnsubscribe();
    this.releasePdf();
    this.programService.clearDownload('ProgramComponent:ngOnDestroy');
  }

  /**
   * Date picker change — reload the products published that day.
   * Future days are ignored; the picker already caps them at today.
   * @param {unknown} event mat-datepicker change event
   * @returns {void}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setDate(event: any): void {
    const rawValue = event?.value ?? event?.target?.value;
    if (!rawValue) return;

    const parsed = moment(rawValue);
    if (!parsed.isValid() || parsed.isAfter(this.maxDate, 'day')) return;

    this.formVal['date'].setValue(parsed.toDate());
    this.fetchPrograms();
  }

  /**
   * Track filter change — narrows the rendered rows client-side
   * @param {unknown} event mat-select change event
   * @returns {void}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectTrack(event: any): void {
    this.selectedTrackId = (event?.value || '') as string;
    this.formVal['eventId'].setValue(this.selectedTrackId);
    this.applyTrackFilter();
    this.cdRef.markForCheck();
  }

  /**
   * Favorites chip — hand the track off to the wager page, matching
   * what the results and replays pages do with their chips
   * @param {IGetEventsDetailsResponse} favorite Clicked favorite event
   * @returns {void}
   */
  selectFavorite(favorite: IGetEventsDetailsResponse): void {
    if (!favorite) return;

    this.router.navigate(['/wager'], {
      state: {
        eventId: favorite?.id,
        runId: favorite?.ToteResponse?.RunId,
        raceId: favorite?.ToteResponse?.EventDetail?.currentRace?.RaceId,
      }
    });
  }

  /**
   * Scroll the favorites row by 200px in the given direction
   * @param {'prev' | 'next'} direction Which side to scroll
   * @returns {void}
   */
  scrollTo(direction: 'prev' | 'next'): void {
    const container = this.favorites?.nativeElement;
    if (!container) return;

    const scrollAmount = 200;

    if (direction === 'prev') container.scrollLeft -= scrollAmount;
    else container.scrollLeft += scrollAmount;
  }

  /**
   * `*ngFor` trackBy for the favorites chips
   * @param {number} _index Chip index (unused)
   * @param {IGetEventsDetailsResponse} item Favorite event
   * @returns {string} Stable event id
   */
  trackFavoriteFn(_index: number, item: IGetEventsDetailsResponse): string {
    return item?.id ?? '';
  }

  /**
   * Open a product. Owned products download straight away; the rest
   * are charged to the account, so they are confirmed first.
   * @param {IDataProduct} product Row that was clicked
   * @returns {void}
   */
  openProduct(product: IDataProduct): void {
    if (!product || this.isLoadingPdf) return;

    if (product.Owned) {
      this.startDownload(product);
      return;
    }

    this.matNotificationService.showAlert(
      'Confirm Purchase',
      `Buy ${product?.ProdName} for $${Number(product?.RetailPrice ?? 0).toFixed(2)}?`,
      undefined,
      'Buy & Download',
      'Cancel'
    )
      .pipe(take(1), takeUntil(this.toUnsubscribe$))
      .subscribe((isConfirmed: boolean) => {
        if (isConfirmed) this.startDownload(product);
      });
  }

  /**
   * Ask the store for a product's PDF and mark its row as the active one
   * @param {IDataProduct} product Product to open
   * @returns {void}
   */
  private startDownload(product: IDataProduct): void {
    this.activeProduct = product;
    this.activeProductId = String(product?.ID ?? '');
    this.downloadError = '';
    this.isLoadingPdf = true;
    this.cdRef.markForCheck();

    this.programService.downloadProgram('ProgramComponent:openProduct', product);
  }

  /**
   * Save the open PDF under the file name the API supplied
   * @returns {void}
   */
  savePdf(): void {
    if (!this.pdfObjectUrl) return;

    const link = document.createElement('a');
    link.href = this.pdfObjectUrl;
    link.download = this.pdfFileName || 'program.pdf';
    link.click();
  }

  /**
   * Open the data product terms and conditions
   * @returns {void}
   */
  openTerms(): void {
    this.dialog.open(ProgramTermsComponent, {
      width: '700px',
      maxWidth: '95vw',
      autoFocus: false,
      panelClass: 'program-terms-dialog'
    });
  }

  /**
   * `*ngFor` trackBy for product rows
   * @param {number} index Row index
   * @param {IDataProduct} item Product
   * @returns {string} Stable row key
   */
  trackProductFn(index: number, item: IDataProduct): string {
    return `${item?.EventId}-${item?.ID}-${index}`;
  }

  /**
   * `*ngFor` trackBy for track options
   * @param {number} _index Option index (unused)
   * @param {IProgramTrack} item Track
   * @returns {string} Track id
   */
  trackTrackFn(_index: number, item: IProgramTrack): string {
    return item?.id;
  }

  /**
   * True while the given row is the one being fetched or displayed
   * @param {IDataProduct} product Row to test
   * @returns {boolean} True when the row is active
   */
  isActiveProduct(product: IDataProduct): boolean {
    return !!this.activeProductId && this.activeProductId === String(product?.ID);
  }

  /**
   * Reload the products for the selected date
   * @returns {void}
   */
  private fetchPrograms(): void {
    this.programService.refreshPrograms(
      'ProgramComponent:fetchPrograms',
      this.formVal['date'].value,
      this.programsState
    );

    // The store may already hold the picked date — render it straight away.
    this.renderPrograms(this.programsState);
  }

  /**
   * Follow the product list the store holds
   * @returns {void}
   */
  private observePrograms(): void {
    this.wagerFacade.getDataPrograms$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((programs?: IDataPrograms) => {
        this.programsState = programs;
        this.renderPrograms(programs);
      });
  }

  /**
   * Render a product list, ignoring one another page loaded for a
   * different date — the skeleton stays up until ours lands
   * @param {IDataPrograms} programs Store slice
   * @returns {void}
   */
  private renderPrograms(programs?: IDataPrograms): void {
    const wanted = this.programService.toApiDate(this.formVal['date'].value);

    if (programs?.date !== wanted || programs?.isProcessing) {
      this.isLoadingPrograms = true;
      this.cdRef.markForCheck();
      return;
    }

    const normalized = this.programService.normalizePrograms(programs?.response);

    this.products = normalized.products;
    this.tracks = normalized.tracks;

    this.sortTracksByFavorites();
    this.applyHandOffTrack();
    this.applyTrackFilter();

    this.isLoadingPrograms = false;
    this.cdRef.markForCheck();
  }

  /**
   * Render the program PDF the store holds, ignoring replies for a row
   * the bettor has already moved on from
   * @returns {void}
   */
  private observeDownloads(): void {
    this.wagerFacade.getDataProgramDownload$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((download?: IDataProgramDownload) => {
        if (!download || download.productId !== this.activeProductId) return;

        this.isLoadingPdf = !!download.isProcessing;
        if (download.isProcessing) {
          this.cdRef.markForCheck();
          return;
        }

        if (download.isSuccess && download.response) {
          this.showPdf(download.response);
        }
        else {
          this.downloadError = this.toDownloadError(download.error);
          this.releasePdf();
        }

        this.cdRef.markForCheck();
      });
  }

  /**
   * Message to show when a download fails, empty when the API sent none
   * @param {unknown} error Error stored with the failed download
   * @returns {string} Message for the viewer pane
   */
  private toDownloadError(error?: unknown): string {
    const body = (error as { error?: { Error?: string, Message?: string } })?.error;

    return body?.Error || body?.Message || '';
  }

  /**
   * Render a downloaded product and flip its row to the owned state
   * @param {string} base64 Base64-encoded PDF
   * @returns {void}
   */
  private showPdf(base64: string): void {
    const objectUrl = this.programService.toPdfObjectUrl(base64);

    if (!objectUrl) {
      this.downloadError = 'The program could not be opened.';
      this.releasePdf();
      return;
    }

    this.releasePdf();

    this.pdfObjectUrl = objectUrl;
    this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl);
    this.pdfFileName = this.activeProduct?.FileName || 'program.pdf';

    // The download doubles as the purchase, so the row is owned now.
    this.markAsOwned(this.activeProduct);
  }

  /**
   * Flip a freshly bought product to owned in both product lists
   * @param {IDataProduct} product Product that was bought
   * @returns {void}
   */
  private markAsOwned(product?: IDataProduct): void {
    if (!product || product?.Owned) return;

    const toOwned = (item: IDataProduct) =>
      item?.ID === product?.ID && item?.EventId === product?.EventId
        ? { ...item, Owned: true }
        : item;

    this.products = this.products.map(toOwned);
    this.applyTrackFilter();
  }

  /**
   * Select the track the wager page handed off, once per navigation
   * @returns {void}
   */
  private applyHandOffTrack(): void {
    if (!this.pendingHandOff) return;

    const trackId = this.programService.resolveTrackId(
      this.tracks,
      this.pendingHandOff.eventId,
      this.pendingHandOff.eventName
    );

    if (trackId) {
      this.selectedTrackId = trackId;
      this.formVal['eventId'].setValue(trackId);
    }

    this.pendingHandOff = undefined;
  }

  /**
   * Re-apply the current track filter to the product list
   * @returns {void}
   */
  private applyTrackFilter(): void {
    this.filteredProducts = this.programService.filterByTrack(
      this.products,
      this.selectedTrackId
    );
  }

  /**
   * Follow the live events and the account favorites, the pair the
   * favorites bar is built from. Mirrors the results / replays pages.
   * @returns {void}
   */
  private observeEventsAndFavorites(): void {
    this.wagerFacade.getSelectAllEventTrackRace$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((allEvents?: IGetEventsDetailsResponse[]) => {
        this.liveEvents = allEvents || [];
        this.rebuildFavoriteEvents();
        this.cdRef.markForCheck();
      });

    this.wagerService.observerPreferenceFavorites()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((listFavorites?: string[]) => {
        this.setListFavorites = new Set(listFavorites || []);
        this.rebuildFavoriteEvents();

        // First emission = favorites loaded, even when empty.
        this.hasEverLoadedFavorites = true;
        this.cdRef.markForCheck();
      });
  }

  /**
   * Rebuild the chips from the favorites the live events still carry
   * @returns {void}
   */
  private rebuildFavoriteEvents(): void {
    this.listOfFavoriteEvents = this.setListFavorites?.size
      ? _.filter(
          this.liveEvents,
          (event: IGetEventsDetailsResponse) =>
            this.setListFavorites.has(event?.id as string)
        )
      : [];

    this.sortTracksByFavorites();
  }

  /**
   * Hoist the favorite tracks to the top of the dropdown, keeping the
   * alphabetical order inside each group
   * @returns {void}
   */
  private sortTracksByFavorites(): void {
    const favoriteIds = this.resolveFavoriteTrackIds();
    const [favorites, others] = _.partition(
      this.tracks,
      (track: IProgramTrack) => favoriteIds.has(track?.id)
    );

    this.favoriteTrackCount = favorites.length;
    this.sortedTracks = [...favorites, ...others];
  }

  /**
   * Program track ids the bettor has favorited. Favorites are stored as
   * tote event ids, so names are matched too when the two ids differ.
   * @returns {Set<string>} Matching program track ids
   */
  private resolveFavoriteTrackIds(): Set<string> {
    const ids = new Set<string>();
    if (!this.setListFavorites?.size) return ids;

    _.forEach(this.tracks, (track: IProgramTrack) => {
      if (this.setListFavorites.has(track?.id)) ids.add(track.id);
    });

    _.forEach(this.listOfFavoriteEvents, (event: IGetEventsDetailsResponse) => {
      const trackId = this.programService.resolveTrackId(
        this.tracks,
        event?.id,
        event?.ToteResponse?.EventDetail?.EventName
      );

      if (trackId) ids.add(trackId);
    });

    return ids;
  }

  /**
   * Drop the open PDF and release its object URL
   * @returns {void}
   */
  private releasePdf(): void {
    if (this.pdfObjectUrl) URL.revokeObjectURL(this.pdfObjectUrl);

    this.pdfObjectUrl = '';
    this.pdfUrl = undefined;
    this.pdfFileName = '';
  }
}
